import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";
import { API, api, money } from "@/lib/api";
import { StepNav } from "@/components/wizard/Stepper";
import { useAuth } from "@/context/AuthContext";

export const productSrc = (order, pid) =>
  `${API}/orders/${order.order_id}/product/${pid}.png?template=${order.template || "classic_scrapbook"}&v=${encodeURIComponent(order.updated_at || "")}`;

const PHOTOS = { ornament: "/img/ornament.png?v=1", ornament_holidays: "/img/ornament-holidays.jpg?v=4", sticker_sheet: "/img/stickers-sheet.jpg?v=2", mug: "/img/mug.jpg?v=1", qr_square: "/img/qr-square.jpg?v=2" };

export const needsDesign = (order) => !(order.items || []).length || (order.items || []).some((i) => i.kind !== "qr_square");

const imgCls = (kind) => (kind?.startsWith("ornament") ? "rounded-full" : "rounded-lg");

const Qty = ({ value, onChange, pid }) => (
  <div className="flex items-center gap-3" data-testid={`qty-control-${pid}`}>
    <button type="button" className="btn-secondary !min-h-0 !p-3" aria-label="Fewer" onClick={() => onChange(Math.max(0, value - 1))} disabled={value === 0} data-testid={`qty-minus-${pid}`}><Minus size={22} /></button>
    <span className="w-10 text-center text-2xl font-bold" data-testid={`qty-value-${pid}`}>{value}</span>
    <button type="button" className="btn-secondary !min-h-0 !p-3" aria-label="More" onClick={() => onChange(Math.min(10, value + 1))} disabled={value === 10} data-testid={`qty-plus-${pid}`}><Plus size={22} /></button>
  </div>
);

export const StepProducts = ({ order, go, back }) => {
  const [products, setProducts] = useState([]);
  const [mode, setMode] = useState(null);
  const { user } = useAuth();
  useEffect(() => { api.get("/config").then(({ data }) => setMode(data.app_mode)); }, []);
  const [qty, setQty] = useState(() => Object.fromEntries((order.items || []).map((i) => [i.product_id, i.quantity])));
  useEffect(() => { api.get("/products").then(({ data }) => setProducts(data)); }, []);
  const total = products.reduce((s, p) => s + p.price * (qty[p.product_id] || 0), 0);
  const count = Object.values(qty).reduce((a, b) => a + b, 0);
  return (
    <div>
      <h2 className="text-3xl font-bold text-[#4A2A18]">Choose your keepsakes</h2>
      <p className="mt-3 text-lg text-[#4A3B32]">Every item carries the same QR code. If you pick the sticker sheet or ornament, you'll choose a design for them next (the Indoor/Outdoor sticker has its own simple "Scan To View Video" look). Pick as many as you like. <b className="text-[#2E6930]">FREE standard shipping</b> to the lower 48 states.</p>
      <div className="mt-8 space-y-5">
        {products.map((p) => {
          const n = qty[p.product_id] || 0;
          return (
            <div key={p.product_id} className={`flex flex-col gap-5 rounded-2xl border-4 bg-white p-4 sm:flex-row sm:items-center ${n ? "border-[#A83232]" : "border-[#E6DEC6]"}`} data-testid={`product-option-${p.product_id}`}>
              {PHOTOS[p.kind] ? (
                <img src={PHOTOS[p.kind]} alt={p.name} className={`mx-auto h-36 w-36 shrink-0 object-contain ${["mug", "ornament_holidays", "qr_square"].includes(p.kind) ? "rounded-lg object-cover shadow-md" : "drop-shadow-md"}`} data-testid={`product-photo-${p.product_id}`} />
              ) : (
                <img src={productSrc(order, p.product_id)} alt={p.name} className={`mx-auto w-36 shrink-0 shadow-md ${imgCls(p.kind)}`} />
              )}
              <div className="flex-1">
                <p className="font-serif text-2xl font-semibold text-[#2C1E16]">{p.name}</p>
                <p className="text-base text-[#5C4A3E]">{p.variant}</p>
                <p className="mt-1 text-base text-[#5C4A3E]">{p.description}</p>
                <p className="mt-2 text-xl font-bold text-[#A83232]" data-testid={`product-price-${p.product_id}`}>{money(p.price)} <span className="text-sm font-semibold text-[#5C4A3E]">each</span><span className="ml-2 inline-block rounded-full bg-[#2E6930] px-3 py-0.5 align-middle text-sm font-bold uppercase tracking-wide text-white" data-testid={`free-shipping-badge-${p.product_id}`}>FREE Shipping</span></p>
              </div>
              <Qty value={n} pid={p.product_id} onChange={(v) => setQty({ ...qty, [p.product_id]: v })} />
            </div>
          );
        })}
      </div>
      <div className="mt-8 flex items-center justify-between rounded-xl bg-[#F2EBE1] p-5 text-xl font-bold" data-testid="cart-summary">
        <span>{count} item{count === 1 ? "" : "s"}</span><span data-testid="cart-total">{money(total)}</span>
      </div>
      {mode === "test" && user?.role === "admin" && (
        <div className="mt-6 rounded-2xl border-2 border-dashed border-[#D97706] bg-[#FFF7E6] p-5" data-testid="no-products-panel">
          <p className="text-lg text-[#4A3B32]"><b>Test mode:</b> just want to post your video and get your QR sticker by email? You can skip the items.</p>
          <button type="button" className="btn-outline mt-4 w-full !text-xl sm:w-auto" onClick={() => go(4, { no_products: true })} data-testid="no-products-button">None, skip items</button>
        </div>
      )}
      <StepNav back={back} disabled={!count} next={() => {
        const plainOnly = products.every((p) => p.kind === "qr_square" || !qty[p.product_id]);
        go(plainOnly ? 5 : 4, { items: products.map((p) => ({ product_id: p.product_id, quantity: qty[p.product_id] || 0 })) });
      }} />
    </div>
  );
};

export const ItemList = ({ order }) => (order.no_products && !(order.items || []).length ? (
  <p className="rounded-xl bg-[#FFF7E6] p-4 text-lg text-[#4A3B32]" data-testid="no-products-note">No items chosen (test mode). We'll post your video and email your QR sticker.</p>
) : (
  <ul className="space-y-3" data-testid="order-items-list">
    {(order.items || []).map((i, n) => (
      <li key={`${i.product_id}-${n}`} className="flex items-center gap-4" data-testid={`order-item-${i.product_id}`}>
        <img src={productSrc(order, i.product_id)} alt="" className={`h-16 shrink-0 object-contain shadow ${imgCls(i.kind)} ${i.kind === "mug" ? "w-28" : "w-16"}`} />
        <span className="flex-1 text-lg"><b>{i.quantity} ×</b> {i.name}<br /><span className="text-sm text-[#5C4A3E]">{i.variant}</span></span>
        <span className="text-lg font-semibold">{money(i.unit_price * i.quantity)}</span>
      </li>
    ))}
  </ul>
));
